import { practiceDataService } from "./practice-data.service";
import type { Lesson, LessonPlan, Question, Sequence, Afb } from "@/features/pfad/types";
import { isIconName } from "@/lib/icons";
import { uuid } from "@/lib/id";

/**
 * Lernpfad-Speicher (liegt in `practice_data.pathLessons`, siehe SCHEMA.md).
 *
 * Ein Array-Eintrag ist eine Lektion; die Sequenz steckt als Name daran.
 * Aufgaben liegen als JSON in der Lektion und werden erst beim ersten Start
 * erzeugt – das hält die Planung des Pfads schnell und vermeidet, dass für
 * Lektionen Aufgaben generiert werden, die nie jemand öffnet.
 */

function newId(): string {
  return uuid();
}

function toLesson(r: Record<string, unknown>, topicId: string): Lesson {
  let raw: unknown = r.questions;
  if (typeof raw === "string" && raw.trim()) { try { raw = JSON.parse(raw); } catch { raw = []; } }
  let questions: Question[] = [];
  let plan: LessonPlan | undefined;
  if (Array.isArray(raw)) questions = raw as Question[];
  else if (raw && typeof raw === "object") {
    const o = raw as { items?: Question[]; plan?: LessonPlan };
    questions = Array.isArray(o.items) ? o.items : [];
    plan = o.plan;
  }
  const afbRaw = Number(r.afb);
  return {
    id: String(r.id ?? ""),
    topic: topicId,
    sequence: String(r.sequence ?? "Lernpfad"),
    seq_pos: Number(r.seq_pos ?? 0),
    position: Number(r.position ?? 0),
    title: String(r.title ?? ""),
    goal: String(r.goal ?? ""),
    icon: isIconName(String(r.icon ?? "")) ? (String(r.icon) as Lesson["icon"]) : "book",
    afb: ((afbRaw === 2 || afbRaw === 3 ? afbRaw : 1) as Afb),
    questions,
    plan,
    source_hash: String(r.source_hash ?? ""),
    done: Boolean(r.done),
    best_pct: Number(r.best_pct ?? 0),
    xp: Number(r.xp ?? 0),
    last_done: String(r.last_done ?? ""),
  };
}

function fromLesson(l: Lesson): Record<string, unknown> {
  return {
    id: l.id,
    sequence: l.sequence,
    seq_pos: l.seq_pos,
    position: l.position,
    title: l.title,
    goal: l.goal,
    icon: l.icon,
    afb: String(l.afb),
    questions: l.plan ? { plan: l.plan, items: l.questions } : l.questions,
    source_hash: l.source_hash,
    done: l.done,
    best_pct: l.best_pct,
    xp: l.xp,
    last_done: l.last_done,
  };
}

export const IMPORTED_SOURCE = "import";

export interface ImportedSection {
  name: string;
  lessons: { title: string; goal: string; icon: string; afb: Afb; questions: Question[] }[];
}

export interface LessonDraft {
  sequence: string;
  seq_pos: number;
  position: number;
  title: string;
  goal: string;
  icon: string;
  afb: Afb;
  plan?: LessonPlan;
}

export const pathService = {
  async listByTopic(topicId: string): Promise<Lesson[]> {
    const { pathLessons } = await practiceDataService.read(topicId);
    return pathLessons
      .map((r) => toLesson(r, topicId))
      .sort((a, b) => a.seq_pos - b.seq_pos || a.position - b.position);
  },

  /** Lektionen zu Sequenzen bündeln – die Anzeige-Struktur des Pfads. */
  toSequences(lessons: Lesson[]): Sequence[] {
    const map = new Map<string, Sequence>();
    for (const l of lessons) {
      const key = `${l.seq_pos}|${l.sequence}`;
      let seq = map.get(key);
      if (!seq) map.set(key, (seq = { name: l.sequence, pos: l.seq_pos, lessons: [] }));
      seq.lessons.push(l);
    }
    return [...map.values()].sort((a, b) => a.pos - b.pos);
  },

  /** Neuen Pfad anlegen. Bestehende Lektionen bleiben unangetastet. */
  async addLessons(topicId: string, drafts: LessonDraft[]): Promise<Lesson[]> {
    const existing = await this.listByTopic(topicId);
    const added: Lesson[] = drafts.map((d) => ({
      id: newId(),
      topic: topicId,
      sequence: d.sequence,
      seq_pos: d.seq_pos,
      position: d.position,
      title: d.title,
      goal: d.goal,
      icon: isIconName(d.icon) ? (d.icon as Lesson["icon"]) : "book",
      afb: d.afb,
      questions: [],
      plan: d.plan,
      source_hash: "",
      done: false,
      best_pct: 0,
      xp: 0,
      last_done: "",
    }));
    const all = [...existing, ...added];
    await practiceDataService.write(topicId, { pathLessons: all.map(fromLesson) });
    return added;
  },

  /** Erzeugte Aufgaben an der Lektion festhalten. */
  async saveQuestions(lesson: Lesson, questions: Question[], sourceHash: string): Promise<void> {
    const lessons = await this.listByTopic(lesson.topic);
    const next = lessons.map((l) =>
      l.id === lesson.id ? { ...l, questions, source_hash: sourceHash } : l
    );
    await practiceDataService.write(lesson.topic, { pathLessons: next.map(fromLesson) });
  },

  /** Ergebnis einer Runde festhalten; `done` bleibt einmal gesetzt bestehen. */
  async recordResult(lesson: Lesson, pct: number, xp: number): Promise<Lesson> {
    const updated: Lesson = {
      ...lesson,
      // Einmal durchgemacht schaltet die nächste Lektion frei, unabhängig vom
      // Ergebnis – ein schwacher Versuch soll nicht den ganzen Pfad blockieren.
      // Ein niedriger `best_pct` bleibt sichtbar und lädt zum Wiederholen ein.
      done: true,
      best_pct: Math.max(lesson.best_pct, Math.round(pct)),
      xp: lesson.xp + xp,
      last_done: new Date().toISOString().slice(0, 10),
    };
    const lessons = await this.listByTopic(lesson.topic);
    const next = lessons.map((l) => (l.id === lesson.id ? updated : l));
    await practiceDataService.write(lesson.topic, { pathLessons: next.map(fromLesson) });
    return updated;
  },

  async importLessons(
    topicId: string,
    sections: ImportedSection[],
    replace: boolean
  ): Promise<{ added: number; updated: number; removed: number }> {
    const existing = await this.listByTopic(topicId);
    const norm = (t: string) => t.trim().toLowerCase().replace(/\s+/g, " ");
    const byTitle = new Map(existing.map((l) => [norm(l.title), l]));
    const result: Lesson[] = replace ? [] : [...existing];
    const seqPos = new Map<string, number>();
    for (const l of result) seqPos.set(l.sequence, l.seq_pos);
    let nextSeq = result.reduce((m, l) => Math.max(m, l.seq_pos + 1), 0);
    let added = 0;
    let updated = 0;

    for (const section of sections) {
      if (!seqPos.has(section.name)) seqPos.set(section.name, nextSeq++);
      const pos = seqPos.get(section.name)!;
      for (const [i, imported] of section.lessons.entries()) {
        const prev = byTitle.get(norm(imported.title));
        const lesson: Lesson = {
          id: prev?.id ?? newId(),
          topic: topicId,
          sequence: section.name,
          seq_pos: pos,
          position: replace ? i : prev?.sequence === section.name ? prev.position : 1000 + i,
          title: imported.title,
          goal: imported.goal,
          icon: isIconName(imported.icon) ? (imported.icon as Lesson["icon"]) : "book",
          afb: imported.afb,
          questions: imported.questions,
          source_hash: IMPORTED_SOURCE,
          done: prev?.done ?? false,
          best_pct: prev?.best_pct ?? 0,
          xp: prev?.xp ?? 0,
          last_done: prev?.last_done ?? "",
        };
        const at = result.findIndex((l) => l.id === lesson.id);
        if (at >= 0) result[at] = lesson;
        else result.push(lesson);
        if (prev) updated++;
        else added++;
      }
    }

    const ordered = result.sort((a, b) => a.seq_pos - b.seq_pos || a.position - b.position);
    ordered.forEach((l, _i, all) => {
      l.position = all.filter((x) => x.seq_pos === l.seq_pos).indexOf(l);
    });
    const kept = new Set(ordered.map((l) => l.id));
    await practiceDataService.write(topicId, { pathLessons: ordered.map(fromLesson) });
    return { added, updated, removed: existing.filter((l) => !kept.has(l.id)).length };
  },

  /** Ganzen Pfad eines Themas verwerfen (Neuaufbau). */
  async clearTopic(topicId: string): Promise<void> {
    await practiceDataService.write(topicId, { pathLessons: [] });
  },
};
